import {useEffect, useRef, useState} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const useWishlistCount = ({product, productId, locationPathname, enabled = false}) => {
    const productObject = product
    const [wishlistCount, setWishlistCount] = useState(productObject?.c_wishlistCount || 0)
    const [shouldFetchFreshLikes, setShouldFetchFreshLikes] = useState(false)
    const queryClient = useQueryClient()

    // Client-side only: Fetch fresh wishlist count on hover to bypass cache
    // As per BD team specification: use c_cp=1 to ensure non-cached near real-time data
    const isClient = typeof window !== 'undefined'

    // For PLP products, we need to get the correct master product ID
    // Products can be: master (has variants), variationGroup, or variant
    // Priority: master.masterId > id > productId
    const masterId =
        productObject?.master?.masterId ||
        productObject?.id ||
        productObject?.productId ||
        productId

    // Seed from the (stale) product prop only the first time we see a given product id.
    // Keying on masterId (not the product object reference) prevents parent re-renders that
    // pass a new product object from clobbering the fresh hover fetch / optimistic updates.
    const seededMasterIdRef = useRef(null)
    useEffect(() => {
        if (seededMasterIdRef.current === masterId) return
        seededMasterIdRef.current = masterId
        setWishlistCount(productObject?.c_wishlistCount || 0)
    }, [masterId, productObject])

    // Reset fetch state when product/masterId changes (e.g., navigating to different PLP without reload)
    // This ensures hover will trigger a fresh fetch for each unique product
    useEffect(() => {
        setShouldFetchFreshLikes(false)
    }, [masterId, enabled])

    // Reset fetch state and invalidate cache when route changes (SPA navigation)
    // This handles the case where user interacts with a product, navigates away, and comes back
    // Without this, React Query's cache prevents fetching fresh data on hover after navigation
    useEffect(() => {
        setShouldFetchFreshLikes(false)
        if (masterId) {
            queryClient.invalidateQueries(['product-wishlist-count', masterId])
        }
    }, [locationPathname, queryClient, masterId])

    // Fetch fresh product data with c_cp=1 and select parameters (BD specification)
    // Using direct fetch because commerce-sdk-react's useProduct doesn't properly pass
    // the select parameter to the API (it gets stripped/ignored by the SDK)
    const {app: appConfig} = getConfig()
    const {getTokenWhenReady} = useAccessToken()
    const appOrigin = useAppOrigin()
    const {site, locale} = useMultiSite()

    // Use isFetching (not isLoading): a disabled query reports isLoading:true until its first
    // fetch, which would show the spinner before the hover-triggered request ever runs.
    const {data: freshProductData, isFetching: isLoadingFreshLikes} = useQuery(
        ['product-wishlist-count', masterId, site?.id, locale?.id],
        async () => {
            if (!masterId) return null

            // Get the access token for authentication
            const token = await getTokenWhenReady()

            // Build the API URL exactly as BD specified (working in Postman)
            // Note: We manually build the query string to avoid URLSearchParams encoding parentheses
            // The select parameter must be: (data.(c_wishlistCount)) not %28data.%28c_wishlistCount%29%29
            const {proxyPath, parameters} = appConfig.commerceAPI
            const organizationId = parameters.organizationId

            const queryParams = [
                `select=(data.(c_wishlistCount))`,
                `ids=${masterId}`,
                `currency=${locale?.preferredCurrency}`,
                `locale=${locale?.id}`,
                `perPricebook=true`,
                `siteId=${site?.id}`,
                `c_cp=1`
            ].join('&')

            const url = `${appOrigin}${proxyPath}/product/shopper-products/v1/organizations/${organizationId}/products?${queryParams}`

            const response = await fetch(url, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token ? `Bearer ${token}` : ''
                }
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()
            // API returns { data: [{ c_wishlistCount: X }] }
            return result?.data?.[0] || null
        },
        {
            enabled: enabled && isClient && shouldFetchFreshLikes && Boolean(masterId),
            staleTime: 30 * 1000, // Match BD backend cache: 30 seconds
            cacheTime: 30 * 1000, // Cache each product independently for 30 seconds
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            // Allow refetch when component remounts with same product (navigation without reload)
            refetchOnReconnect: false,
            // Keep previous data while fetching to avoid flickering
            keepPreviousData: false
        }
    )

    // Update wishlist count when fresh data arrives
    useEffect(() => {
        if (freshProductData?.c_wishlistCount !== undefined) {
            setWishlistCount(freshProductData.c_wishlistCount)
        }
    }, [freshProductData])

    const triggerFreshLikesFetch = () => {
        if (enabled && !shouldFetchFreshLikes) {
            setShouldFetchFreshLikes(true)
        }
    }

    return {
        wishlistCount,
        setWishlistCount,
        isLoadingFreshLikes,
        triggerFreshLikesFetch,
        masterId
    }
}

export default useWishlistCount
